import { Button, List } from 'antd';
import React, { useEffect, useState } from 'react';
import { Icon, Text, UserAvatar } from '@common/components';
import { useMutation, useNavigate } from '@common/hooks';
import {
  AccountAvatar,
  SignInEmailForm,
  SignInEmailFormRecordType,
} from '@identity/components';
import { SignOutAll } from '@identity/graphql';
import Auth from 'works/legacy/lib/auth';
import { createMessage } from 'works/legacy/lib/message-helper';
import { Colors, FontSize, FontWeight, LineHeight } from '@common/tokens';

type Props = {
  initialEmail?: string;
  sessionExpired: boolean;
  newSession: string | null;
  setWorksAuthInfo: (info: SignInEmailFormRecordType | null) => void;
  setTitle: (title: string) => void;
};

export const SignInPageActiveAccountsList = ({
  initialEmail,
  sessionExpired,
  newSession,
  setWorksAuthInfo,
  setTitle,
}: Props) => {
  setTitle("Let's get to work.");

  const [accounts, setAccounts] = useState(Auth.allAccounts());
  const navigate = useNavigate();
  const [logoutAllAccounts, { loading }] = useMutation(SignOutAll, {
    onCompleted: (data) => {
      if (!data.signOutAll.success) return;

      Auth.reset();
      setAccounts(Auth.allAccounts());
    },
    onError: () => {
      createMessage('error', 'Failed to logout all accounts.');
    },
  });

  useEffect(() => {
    if (sessionExpired && !newSession) {
      createMessage('error', 'Your session has expired.');
      Auth.signOut();
      setAccounts(Auth.allAccounts());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="sign-in-page-active-accounts-list">
      <SignInEmailForm
        initialEmail={initialEmail}
        onFinish={setWorksAuthInfo}
      />

      {accounts.length > 0 && (
        <List
          className="active-accounts"
          itemLayout="horizontal"
          dataSource={accounts}
          style={{ width: '100%' }}
          footer={
            <Button
              type="link"
              className="logout-all-accounts-link"
              icon={
                <Icon
                  className="logout-link-icon"
                  icon="rightFromBracket"
                  kind="light"
                  size="md"
                />
              }
              onClick={() => {
                logoutAllAccounts({
                  variables: {
                    input: Auth.signOutAllData(),
                  },
                });
              }}
              loading={loading}
            >
              Logout All Accounts
            </Button>
          }
          header={<Text color="midnightLighter">Active Account(s):</Text>}
          renderItem={(account) => (
            <List.Item
              onClick={() => {
                Auth.switch(account.membershipId);
                navigate(account.path, { replace: true });
              }}
              className="sentry-mask"
            >
              <List.Item.Meta
                className="item-meta"
                avatar={
                  <>
                    <UserAvatar
                      size={40}
                      backgroundColor={Colors.elmDarkest}
                      textAsInitials={account.name}
                    />
                    <AccountAvatar
                      style={{ marginLeft: '-8px' }}
                      account={account}
                      size={40}
                    />
                  </>
                }
                title={account.entityName}
                description={account.email}
              />
            </List.Item>
          )}
        />
      )}

      <style jsx>
        {`
          .sign-in-page-active-accounts-list {
            width: 100%;
          }

          .sign-in-page-active-accounts-list :global(.ant-list-header) {
            border-bottom: 0;
          }

          .sign-in-page-active-accounts-list :global(.active-accounts) {
            box-shadow: 0 4px 12px 0 #2423291f;
            margin-bottom: 18px;
          }

          .sign-in-page-active-accounts-list
            :global(.ant-avatar, .ant-list-item, .ant-list-item-meta) {
            cursor: pointer;
          }

          .sign-in-page-active-accounts-list :global(.ant-list-item) {
            border-bottom: 0;
          }

          .sign-in-page-active-accounts-list :global(.ant-list-item-meta) {
            align-items: center;
            align-content: center;
          }

          .sign-in-page-active-accounts-list
            :global(.ant-list-item-meta-description) {
            color: ${Colors.midnightLighter};
            font-size: ${FontSize.xs};
            line-height: ${LineHeight.xs};
          }

          .sign-in-page-active-accounts-list
            :global(.ant-list-item-meta-content) {
            align-self: center;
          }

          .sign-in-page-active-accounts-list
            :global(.ant-list-item-meta-title) {
            font-weight: ${FontWeight.medium};
            line-height: ${LineHeight.xs};
          }

          .sign-in-page-active-accounts-list :global(.ant-list-header) {
            padding-left: 12px;
          }

          .sign-in-page-active-accounts-list :global(.ant-list-item) {
            padding-left: 12px;
          }

          .sign-in-page-active-accounts-list :global(.ant-list-item):hover {
            background-color: ${Colors.elmHyperlight};
          }

          .sign-in-page-active-accounts-list
            :global(.logout-all-accounts-link) {
            display: flex;
            align-items: center;
            background-color: transparent;
            border: 0;
            margin: 0;
            color: ${Colors.textDefault};
            font-weight: 400;
          }

          .sign-in-page-active-accounts-list
            :global(
              .logout-all-accounts-link > .ant-btn-loading-icon,
              .logout-all-accounts-link > .logout-link-icon
            ) {
            align-self: center;
            padding: 4px 16px 4px 4px;
          }

          .sign-in-page-active-accounts-list
            :global(.logout-all-accounts-link):hover {
            color: ${Colors.elmDarkest};
            background-color: transparent;
            border-color: transparent;
          }

          .sign-in-page-active-accounts-list
            :global(.logout-all-accounts-link):focus {
            color: ${Colors.elmDarkest};
            background-color: transparent;
            border-color: transparent;
          }
        `}
      </style>
    </div>
  );
};
